/**
 * PaymentManagement — Phase 03 redesign
 * Dense 40px rows + right drawer, react-query
 */
import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Icons,
  KPI, KPIStrip,
  Drawer, DrawerBody, DrawerSection, DrawerFooter, KVList,
  EmptyState,
  CurrencyInput,
} from '@/shared/ui';
import api from '../../utils/api';
import ManualPaymentModal from './ManualPaymentModal';
import BillExportCard from './BillExportCard';

const intlLocale = (lang) => (lang === 'en' ? 'en-US' : 'vi-VN');

const VND = (n, lang = 'vi') =>
  new Intl.NumberFormat(intlLocale(lang), { style: 'currency', currency: 'VND' }).format(n ?? 0);

const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Prepaid-block marker the finance service writes into other_costs_detail.
// type 'prepaid_block'   -> this bill carries N months of rent + services
// type 'prepaid_covered' -> a month already paid for by an earlier bill
const prepaidInfo = (payment) => {
  let d = payment?.other_costs_detail;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch { return null; } }
  const line = d?.prepaid_block;
  if (!line) return null;
  return {
    covered: line.type === 'prepaid_covered',
    months: line.prepaid_months ?? line.multiplier ?? null,
    name: line.name || '',
  };
};

function useStatusMeta() {
  const { t } = useTranslation('finance');
  return {
    confirmed: { label: t('status.confirmed'), cls: 'text-green-700 bg-green-50  ring-green-200' },
    partial:   { label: t('status.partial'),   cls: 'text-amber-700 bg-amber-50  ring-amber-200' },
    pending:   { label: t('status.pending'),   cls: 'text-teal-700  bg-teal-50   ring-teal-200'  },
    rejected:  { label: t('status.rejected'),  cls: 'text-red-700   bg-red-50    ring-red-200'   },
  };
}

function StatusPill({ status }) {
  const meta = useStatusMeta();
  const m = meta[status] ?? { label: status, cls: 'text-stone-600 bg-stone-100 ring-stone-200' };
  return (
    <span className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ── Confirm / reject inline modal ─────────────────────────────────────────────
function ConfirmModal({ confirmAction, onClose, onSubmit, loading }) {
  const { t, i18n } = useTranslation(['finance', 'common']);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const { payment, action } = confirmAction;
  const [adminNote, setAdminNote] = useState('');
  const [actualAmount, setActualAmount] = useState(
    !['reject', 'revert'].includes(action) ? String(payment.final_amount ?? payment.invoice_amount ?? '') : ''
  );

  const meta = {
    confirm: { label: t('finance:payment.action.confirm'), btnCls: 'bg-green-600 hover:bg-green-700', status: 'confirmed' },
    reject:  { label: t('finance:payment.action.reject'),  btnCls: 'bg-red-600   hover:bg-red-700',   status: 'rejected'  },
    partial: { label: t('finance:payment.action.partial'), btnCls: 'bg-amber-500 hover:bg-amber-600', status: 'partial'   },
    revert:  { label: 'Hoàn tác về chờ xử lý',            btnCls: 'bg-stone-600 hover:bg-stone-700', status: 'pending'   },
  }[action];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/40" onClick={onClose}>
      <div className="bg-white rounded-xl border border-stone-200 shadow-2xl w-full max-w-sm mx-4 p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-stone-900">
          {t('finance:payment.confirmModal.title', { action: meta.label })}
        </h3>
        <p className="text-[13px] text-stone-600">
          {t('finance:payment.confirmModal.invoiceLine', {
            id: String(payment.id).padStart(4, '0'),
            renter: payment.renter_name,
            room: payment.room_number,
          })}
        </p>
        {!['reject', 'revert'].includes(action) && (
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">
              {t('finance:payment.confirmModal.actualAmount')}
            </label>
            <CurrencyInput
              value={actualAmount}
              onChange={e => setActualAmount(e.target.value)}
              className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
            />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            {t('finance:payment.confirmModal.note')}
          </label>
          <textarea
            value={adminNote}
            onChange={e => setAdminNote(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
            placeholder={t('finance:payment.confirmModal.notePlaceholder')}
          />
        </div>
        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            {t('common:action.cancel')}
          </button>
          <button
            onClick={() => onSubmit({
              status: meta.status,
              admin_note: adminNote,
              actual_amount: actualAmount ? Number(actualAmount) : undefined,
            })}
            disabled={loading}
            className={`flex-1 h-9 rounded-lg text-white text-[13px] font-medium transition-colors disabled:opacity-50 ${meta.btnCls}`}
          >
            {loading ? t('finance:payment.confirmModal.processing') : meta.label}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function useTabs() {
  const { t } = useTranslation('finance');
  return [
    { key: 'all',       label: t('payment.tab.all') },
    { key: 'confirmed', label: t('payment.tab.confirmed') },
    { key: 'pending',   label: t('payment.tab.pending') },
    { key: 'overdue',   label: t('payment.tab.overdue') },
    { key: 'partial',   label: t('payment.tab.partial') },
  ];
}

export default function PaymentManagement() {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation(['finance', 'common']);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const TABS = useTabs();

  const [month, setMonth] = useState(nowYM);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [drawerPayment, setDrawerPayment] = useState(null);
  const [drawerTab, setDrawerTab] = useState('detail');
  const [surchargeForm, setSurchargeForm] = useState({ label: '', amount_per_unit: '', unit_type: 'flat', quantity: 1 });
  const [showManual, setShowManual] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showBulkSurcharge, setShowBulkSurcharge] = useState(false);
  const [showVacation, setShowVacation] = useState(false);
  const [showPrepay, setShowPrepay] = useState(false);
  const [prepayForm, setPrepayForm] = useState({ start_month: '', months: 3, discount_amount: 0 });
  const [confirmAction, setConfirmAction] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const billCardRef = useRef(null);

  const handleExportBill = useCallback(async () => {
    if (!billCardRef.current || !drawerPayment) return;
    setIsExporting(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(billCardRef.current, { pixelRatio: 2, backgroundColor: '#ffffff' });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `hoa-don-phong-${drawerPayment.room_number || drawerPayment.id}-${drawerPayment.month || 'unknown'}.png`;
      a.click();
    } catch {
      toast.error('Không thể xuất hóa đơn');
    } finally {
      setIsExporting(false);
    }
  }, [drawerPayment]);

  // ── data ──────────────────────────────────────────────────────────────────────
  const { data: allPayments = [], isLoading } = useQuery({
    queryKey: ['payments-admin'],
    queryFn: () => api.get('/api/admin/payments').then(r => r.data ?? []),
    staleTime: 2 * 60 * 1000,
  });

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings-filter'],
    queryFn: () =>
      api.get('/api/properties/buildings').then(r => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.buildings ?? []);
      }),
    staleTime: 10 * 60 * 1000,
  });

  const { data: convData } = useQuery({
    queryKey: ['payment-conv', drawerPayment?.id],
    queryFn: () =>
      api.get(`/api/admin/payments/${drawerPayment.id}/conversation`).then(r => r.data),
    enabled: !!drawerPayment,
    staleTime: 60 * 1000,
  });

  // Fetch meter readings billed to this invoice's month.
  // Readings are stored under the month they were TAKEN but billing_month = next month
  // (arrears billing). So a June invoice should show readings where billing_month=2026-06,
  // which are physically taken in May.
  const { data: meterReadings = [] } = useQuery({
    queryKey: ['payment-meters', drawerPayment?.room_id, drawerPayment?.month],
    queryFn: () =>
      api.get('/api/admin/meter-readings', {
        params: { room_id: drawerPayment.room_id, billing_month: drawerPayment.month, limit: 20 },
      }).then(r => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.readings ?? []);
      }).catch(() => []),
    enabled: !!drawerPayment?.room_id && !!drawerPayment?.month,
    staleTime: 30 * 1000,
  });

  // Vacations recorded for this contract (renter away, keeps room)
  const { data: vacations = [] } = useQuery({
    queryKey: ['payment-vacations', drawerPayment?.contract_id],
    queryFn: () =>
      api.get(`/api/admin/contracts/${drawerPayment.contract_id}/vacations`)
        .then(r => r.data?.vacations ?? [])
        .catch(() => []),
    enabled: !!drawerPayment?.contract_id,
    staleTime: 30 * 1000,
  });

  // Prepaid blocks for this contract (renter paid N months up front)
  const { data: prepayments = [] } = useQuery({
    queryKey: ['payment-prepayments', drawerPayment?.contract_id],
    queryFn: () =>
      api.get(`/api/admin/contracts/${drawerPayment.contract_id}/prepayments`)
        .then(r => r.data?.prepayments ?? [])
        .catch(() => []),
    enabled: !!drawerPayment?.contract_id,
    staleTime: 30 * 1000,
  });

  // ── mutations ─────────────────────────────────────────────────────────────────
  const updateStatus = useMutation({
    mutationFn: ({ id, status, admin_note, actual_amount }) =>
      api.put(`/api/admin/payments/${id}/status`, { status, admin_note, actual_amount }).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['payments-admin'] });
      setConfirmAction(null);
      if (drawerPayment?.id === vars.id) {
        setDrawerPayment(prev => prev ? { ...prev, status: vars.status } : prev);
      }
    },
    // Without this the modal just sits there on a failed confirm with no feedback
    onError: (err) => toast.error(err?.response?.data?.detail || 'Cập nhật trạng thái thất bại'),
  });

  const addSurcharge = useMutation({
    mutationFn: ({ paymentId, body }) =>
      api.post(`/api/admin/payments/${paymentId}/surcharges`, body).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['payments-admin'] });
      setDrawerPayment(prev => prev ? { ...prev, surcharges: data.surcharges, final_amount: data.final_amount } : prev);
      setSurchargeForm({ label: '', amount_per_unit: '', unit_type: 'flat', quantity: 1 });
      toast.success('Đã thêm phụ phí');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Thêm phụ phí thất bại'),
  });

  const removeSurcharge = useMutation({
    mutationFn: ({ paymentId, surchargeId }) =>
      api.delete(`/api/admin/payments/${paymentId}/surcharges/${surchargeId}`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['payments-admin'] });
      setDrawerPayment(prev => prev ? { ...prev, surcharges: data.surcharges, final_amount: data.final_amount } : prev);
      toast.success('Đã xóa phụ phí');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Xóa phụ phí thất bại'),
  });

  const addVacation = useMutation({
    mutationFn: ({ paymentId, body }) =>
      api.post(`/api/admin/payments/${paymentId}/vacation`, body).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['payments-admin'] });
      qc.invalidateQueries({ queryKey: ['payment-vacations'] });
      setShowVacation(false);
      // Patch the open drawer in place so the discount line + badge show immediately
      if (data?.payment) {
        setDrawerPayment(prev => prev ? { ...prev, ...data.payment } : prev);
      }
      toast.success('Đã ghi nhận nghỉ — hóa đơn các tháng liên quan đã cập nhật');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Ghi nhận nghỉ thất bại'),
  });

  const cancelVacation = useMutation({
    mutationFn: (vacationId) =>
      api.delete(`/api/admin/vacations/${vacationId}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payments-admin'] });
      qc.invalidateQueries({ queryKey: ['payment-vacations'] });
      toast.success('Đã hủy kỳ nghỉ — hóa đơn liên quan đã cập nhật');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Hủy kỳ nghỉ thất bại'),
  });

  const invalidatePrepay = () => {
    qc.invalidateQueries({ queryKey: ['payments-admin'] });
    qc.invalidateQueries({ queryKey: ['payment-prepayments'] });
    qc.invalidateQueries({ queryKey: ['contract-prepayments'] });
  };

  const addPrepayment = useMutation({
    mutationFn: ({ contractId, body }) =>
      api.post(`/api/admin/contracts/${contractId}/prepayments`, body).then(r => r.data),
    onSuccess: (d) => {
      invalidatePrepay();
      setShowPrepay(false);
      setPrepayForm({ start_month: '', months: 3, discount_amount: 0 });
      const n = d?.payments_recomputed ?? 0;
      toast.success(n > 0
        ? `Đã ghi nhận trả trước — ${n} kỳ đã cập nhật`
        : 'Đã ghi nhận trả trước — sẽ áp dụng khi tạo kỳ thanh toán');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Lưu trả trước thất bại'),
  });

  const cancelPrepayment = useMutation({
    mutationFn: (prepaymentId) =>
      api.delete(`/api/admin/prepayments/${prepaymentId}`).then(r => r.data),
    onSuccess: () => {
      invalidatePrepay();
      toast.success('Đã hủy kỳ trả trước — hóa đơn liên quan đã cập nhật');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Hủy trả trước thất bại'),
  });

  // ── computed ──────────────────────────────────────────────────────────────────
  const currentYM = nowYM();

  const monthPayments = useMemo(
    () => allPayments.filter(p => (p.month ?? '').startsWith(month)),
    [allPayments, month]
  );

  // Apply building filter early so KPI + tab counts stay in sync with the table
  const filteredByBuilding = useMemo(
    () => buildingId
      ? monthPayments.filter(p => String(p.building_id) === String(buildingId))
      : monthPayments,
    [monthPayments, buildingId]
  );

  const kpi = useMemo(() => {
    const total = filteredByBuilding.reduce((s, p) => s + (p.final_amount ?? p.invoice_amount ?? 0), 0);
    const paid  = filteredByBuilding
      .filter(p => p.status === 'confirmed' || p.status === 'paid')
      .reduce((s, p) => s + (p.paid_amount ?? 0), 0);
    const pending = filteredByBuilding.filter(p => p.status === 'pending').length;
    return { total, paid, pending, outstanding: total - paid };
  }, [filteredByBuilding]);

  const tabCounts = useMemo(() => ({
    all:       filteredByBuilding.length,
    confirmed: filteredByBuilding.filter(p => p.status === 'confirmed').length,
    pending:   filteredByBuilding.filter(p => p.status === 'pending').length,
    partial:   filteredByBuilding.filter(p => p.status === 'partial').length,
    overdue:   filteredByBuilding.filter(p => p.status === 'pending' && month < currentYM).length,
  }), [filteredByBuilding, month, currentYM]);

  const rows = useMemo(() => {
    let list = filteredByBuilding.filter(p => {
      if (tab === 'confirmed') return p.status === 'confirmed';
      if (tab === 'pending')   return p.status === 'pending';
      if (tab === 'partial')   return p.status === 'partial';
      if (tab === 'overdue')   return p.status === 'pending' && month < currentYM;
      return true;
    });
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.room_number?.toLowerCase().includes(q) ||
        p.renter_name?.toLowerCase().includes(q) ||
        String(p.id).includes(q)
      );
    }
    return list;
  }, [filteredByBuilding, tab, search, month, currentYM]);

  // ── selection ─────────────────────────────────────────────────────────────────
  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(s => { const n = new Set(s); rows.forEach(r => n.delete(r.id)); return n; });
    } else {
      setSelected(s => { const n = new Set(s); rows.forEach(r => n.add(r.id)); return n; });
    }
  };
  const toggleRow = (id) =>
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const txList = convData ? (convData.transactions ?? convData) : null;

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">{t('finance:payment.title')}</h1>
          <p className="text-sm text-stone-500 mt-0.5">{t('finance:payment.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowManual(true)}
            className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            <Icons.Plus size={14} />
            {t('finance:payment.addManual')}
          </button>
          <button
            onClick={() => setShowGenerate(true)}
            className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-teal-300 text-teal-700 text-[13px] font-medium hover:bg-teal-50 transition-colors"
          >
            <Icons.Sparkles size={14} />
            Tạo hóa đơn
          </button>
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['payments-admin'] })}
            className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors"
          >
            <Icons.Refresh size={14} />
            {t('common:action.refresh')}
          </button>
        </div>
      </div>

      {/* ── KPI strip ── */}
      <KPIStrip>
        <KPI
          label={t('finance:payment.kpi.revenue')}
          value={VND(kpi.total, lang)}
          icon={<Icons.Chart />}
          accent="primary"
        />
        <KPI
          label={t('finance:payment.kpi.collected')}
          value={VND(kpi.paid, lang)}
          icon={<Icons.CheckCircle />}
          accent="success"
        />
        <KPI
          label={t('finance:payment.kpi.pending')}
          value={String(kpi.pending)}
          sub={t('finance:kpi.pendingUnit')}
          icon={<Icons.Clock />}
          accent="warning"
        />
        <KPI
          label={t('finance:payment.kpi.outstanding')}
          value={VND(kpi.outstanding, lang)}
          icon={<Icons.AlertCircle />}
          accent="danger"
        />
      </KPIStrip>

      {/* ── Tab strip ── */}
      <div className="flex items-center gap-1 border-b border-stone-200 overflow-x-auto">
        {TABS.map(tb => (
          <button
            key={tb.key}
            onClick={() => { setTab(tb.key); setSelected(new Set()); }}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium border-b-2 whitespace-nowrap transition-colors -mb-px ${
              tab === tb.key
                ? 'border-teal-600 text-teal-700'
                : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            {tb.label}
            {tabCounts[tb.key] > 0 && (
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                tab === tb.key ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-stone-500'
              }`}>
                {tabCounts[tb.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Filter row ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="month"
          value={month}
          onChange={e => { setMonth(e.target.value); setSelected(new Set()); }}
          className="h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
        />
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Icons.Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
          />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('finance:payment.filter.searchPlaceholder')}
            className="w-full h-9 pl-8 pr-3 rounded-lg border border-stone-200 text-[13px] placeholder:text-stone-400 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          />
        </div>
        {buildings.length > 0 && (
          <select
            value={buildingId}
            onChange={e => setBuildingId(e.target.value)}
            className="h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          >
            <option value="">{t('finance:payment.filter.buildingAll')}</option>
            {buildings.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
        <span className="ml-auto text-xs text-stone-400">
          {t('finance:payment.filter.resultCount', { count: rows.length })}
        </span>
      </div>

      {/* ── Bulk action bar ── */}
      {selected.size > 0 && (
        <div className="px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-lg flex flex-wrap items-center gap-2">
          {/* Count badge — always visible, full-width on its own line on very small screens */}
          <span className="text-[12px] font-semibold text-teal-800 whitespace-nowrap">
            {t('finance:payment.bulk.selected', { count: selected.size })}
          </span>

          {/* Spacer pushes buttons right on wider screens */}
          <div className="flex flex-wrap items-center gap-1.5 ml-auto">
            {/* Confirm all */}
            <button
              onClick={() => {
                [...selected].forEach(id =>
                  updateStatus.mutate({ id, status: 'confirmed', admin_note: '' })
                );
                setSelected(new Set());
              }}
              className="flex items-center gap-1 px-2.5 h-8 rounded-lg bg-green-600 text-white text-[12px] font-medium hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              <Icons.CheckCircle size={13} />
              <span className="hidden sm:inline">Xác nhận</span>
              <span className="sm:hidden">✓</span>
            </button>

            {/* Bulk revert — only when some selected are non-pending */}
            {(() => {
              const revertable = rows.filter(p => selected.has(p.id) && p.status !== 'pending');
              if (revertable.length === 0) return null;
              return (
                <button
                  onClick={() => {
                    revertable.forEach(p =>
                      updateStatus.mutate({ id: p.id, status: 'pending', admin_note: '' })
                    );
                    setSelected(new Set());
                  }}
                  className="flex items-center gap-1 px-2.5 h-8 rounded-lg bg-stone-600 text-white text-[12px] font-medium hover:bg-stone-700 transition-colors whitespace-nowrap"
                >
                  <Icons.Refresh size={13} />
                  <span className="hidden sm:inline">Hoàn tác</span>
                  <span className="sm:hidden">↺</span>
                  <span className="ml-0.5">({revertable.length})</span>
                </button>
              );
            })()}

            {/* Bulk surcharge */}
            <button
              onClick={() => setShowBulkSurcharge(true)}
              className="flex items-center gap-1 px-2.5 h-8 rounded-lg bg-violet-600 text-white text-[12px] font-medium hover:bg-violet-700 transition-colors whitespace-nowrap"
            >
              <Icons.Plus size={13} />
              <span className="hidden sm:inline">Phụ phí</span>
              <span className="sm:hidden">+₫</span>
            </button>

            {/* Deselect */}
            <button
              onClick={() => setSelected(new Set())}
              className="px-2.5 h-8 rounded-lg border border-stone-200 text-[12px] font-medium text-stone-600 hover:bg-stone-50 transition-colors whitespace-nowrap"
            >
              <span className="hidden sm:inline">{t('finance:payment.bulk.deselect')}</span>
              <span className="sm:hidden">✕</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Dense table ── */}
      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-[13px] min-w-[520px]">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="w-10 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:payment.col.invoice')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:payment.col.renter')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden md:table-cell">
                {t('finance:payment.col.room')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">
                {t('finance:payment.col.month')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:payment.col.status')}
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:payment.col.amount')}
              </th>
              <th className="w-20 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="h-10">
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="px-3">
                      <div className="h-3.5 bg-stone-100 rounded animate-pulse" style={{ width: j === 6 ? '60%' : '80%', marginLeft: j === 6 ? 'auto' : undefined }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    compact
                    icon={<Icons.Receipt />}
                    title={t('finance:payment.empty.title')}
                    description={t('finance:payment.empty.description')}
                  />
                </td>
              </tr>
            ) : (
              rows.map(p => (
                <tr
                  key={p.id}
                  onClick={() => { setDrawerPayment(p); setDrawerTab('detail'); setSurchargeForm({ label: '', amount_per_unit: '', unit_type: 'flat', quantity: 1 }); }}
                  className={`h-10 cursor-pointer transition-colors hover:bg-stone-50 ${
                    selected.has(p.id) ? 'bg-teal-50/60' : ''
                  }`}
                >
                  <td className="px-3" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleRow(p.id)}
                      className="rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                    />
                  </td>
                  <td className="px-3 font-mono text-stone-700 font-medium">
                    #{String(p.id).padStart(4, '0')}
                  </td>
                  <td className="px-3 text-stone-800 max-w-[140px] truncate">
                    {p.renter_name || '—'}
                  </td>
                  <td className="px-3 text-stone-500 hidden md:table-cell">
                    {p.room_number || '—'}
                  </td>
                  <td className="px-3 text-stone-500 hidden sm:table-cell">
                    <span className="inline-flex items-center gap-1.5">
                      {p.month || '—'}
                      {(() => {
                        const pre = prepaidInfo(p);
                        if (!pre) return null;
                        return pre.covered ? (
                          <span title={pre.name} className="px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-semibold ring-1 ring-teal-200">
                            ✓ đã trả trước
                          </span>
                        ) : (
                          <span title={pre.name} className="px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-800 text-[10px] font-semibold">
                            💰 {pre.months} tháng
                          </span>
                        );
                      })()}
                    </span>
                  </td>
                  <td className="px-3">
                    <StatusPill status={p.status} />
                  </td>
                  <td className="px-3 text-right font-medium text-stone-900 tabular-nums">
                    {VND(p.final_amount ?? p.invoice_amount, lang)}
                  </td>
                  <td className="px-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {p.status === 'pending' && (
                        <button
                          onClick={() => setConfirmAction({ payment: p, action: 'confirm' })}
                          title={t('finance:payment.action.confirm')}
                          className="p-1.5 rounded-md text-green-600 hover:bg-green-50 transition-colors"
                        >
                          <Icons.CheckCircle size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => { setDrawerPayment(p); setDrawerTab('detail'); setSurchargeForm({ label: '', amount_per_unit: '', unit_type: 'flat', quantity: 1 }); }}
                        title={t('finance:payment.action.detail')}
                        className="p-1.5 rounded-md text-stone-400 hover:bg-stone-100 transition-colors"
                      >
                        <Icons.Eye size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* ── Detail drawer ── */}
      <Drawer
        open={!!drawerPayment}
        onClose={() => setDrawerPayment(null)}
        title={drawerPayment ? t('finance:payment.drawer.titleInvoice', { id: String(drawerPayment.id).padStart(4, '0') }) : ''}
        subtitle={
          drawerPayment
            ? `${drawerPayment.room_number || ''}${drawerPayment.month ? ` · ${drawerPayment.month}` : ''}`
            : ''
        }
        width={400}
      >
        {drawerPayment && (
          <>
            <DrawerBody>
              {/* ── Drawer tabs ── */}
              <div className="flex border-b border-stone-200 mb-0 -mx-0">
                {[
                  { key: 'detail', label: 'Chi tiết' },
                  { key: 'surcharges', label: `Phụ phí${(drawerPayment.surcharges?.length ?? 0) > 0 ? ` (${drawerPayment.surcharges.length})` : ''}` },
                ].map(t => (
                  <button
                    key={t.key}
                    onClick={() => setDrawerTab(t.key)}
                    className={`px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
                      drawerTab === t.key
                        ? 'border-teal-600 text-teal-700'
                        : 'border-transparent text-stone-500 hover:text-stone-700'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* ── Phụ phí tab ── */}
              {drawerTab === 'surcharges' && (
                <>
                  {/* Existing surcharges */}
                  <DrawerSection title="Danh sách phụ phí">
                    {(!drawerPayment.surcharges || drawerPayment.surcharges.length === 0) ? (
                      <p className="text-[13px] text-stone-400 italic">Chưa có phụ phí nào</p>
                    ) : (
                      <div className="space-y-1.5">
                        {drawerPayment.surcharges.map(s => (
                          <div key={s.id} className="flex items-center justify-between gap-2 py-2 border-b border-stone-100 last:border-0">
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-medium text-stone-800 truncate">{s.label}</p>
                              <p className="text-[11px] text-stone-400 mt-0.5">
                                {VND(s.amount_per_unit, lang)}
                                {s.unit_type === 'per_person' ? '/người' : ''}
                                {s.quantity > 1 ? ` × ${s.quantity}` : ''}
                                {s.unit_type === 'per_person' ? ` người` : ''}
                              </p>
                            </div>
                            <span className="text-[13px] font-semibold text-violet-700 tabular-nums whitespace-nowrap">
                              {VND(s.total, lang)}
                            </span>
                            <button
                              onClick={() => removeSurcharge.mutate({ paymentId: drawerPayment.id, surchargeId: s.id })}
                              disabled={removeSurcharge.isPending}
                              className="p-1.5 rounded-md text-rose-400 hover:bg-rose-50 transition-colors disabled:opacity-40"
                              title="Xóa phụ phí"
                            >
                              <Icons.XCircle size={13} />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-baseline justify-between pt-2 border-t border-stone-200 mt-1">
                          <span className="text-[12px] font-semibold text-stone-600">Tổng phụ phí</span>
                          <span className="text-[13px] font-bold text-violet-700 tabular-nums">
                            {VND((drawerPayment.surcharges || []).reduce((s, x) => s + (x.total || 0), 0), lang)}
                          </span>
                        </div>
                      </div>
                    )}
                  </DrawerSection>

                  {/* Add surcharge form */}
                  <DrawerSection title="Thêm phụ phí">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Tên phụ phí *</label>
                        <input
                          value={surchargeForm.label}
                          onChange={e => setSurchargeForm(f => ({ ...f, label: e.target.value }))}
                          placeholder="VD: Tạm trú, Phí vệ sinh…"
                          className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Đơn giá *</label>
                          <CurrencyInput
                            value={surchargeForm.amount_per_unit}
                            onChange={e => setSurchargeForm(f => ({ ...f, amount_per_unit: e.target.value }))}
                            className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Loại</label>
                          <select
                            value={surchargeForm.unit_type}
                            onChange={e => setSurchargeForm(f => ({ ...f, unit_type: e.target.value }))}
                            className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                          >
                            <option value="flat">Cố định</option>
                            <option value="per_person">Theo người</option>
                          </select>
                        </div>
                      </div>
                      {surchargeForm.unit_type === 'flat' && (
                        <div>
                          <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Số lượng</label>
                          <input
                            type="number"
                            min={1}
                            value={surchargeForm.quantity}
                            onChange={e => setSurchargeForm(f => ({ ...f, quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
                            className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                          />
                        </div>
                      )}
                      {surchargeForm.unit_type === 'per_person' && (
                        <p className="text-[11px] text-teal-600 bg-teal-50 rounded-lg px-3 py-2">
                          Số lượng sẽ tự động lấy theo số người trong hợp đồng
                        </p>
                      )}
                      {surchargeForm.amount_per_unit && surchargeForm.label && (
                        <p className="text-[12px] text-stone-500">
                          Tổng:{' '}
                          <strong className="text-violet-700">
                            {VND(
                              parseFloat(surchargeForm.amount_per_unit || 0) *
                              (surchargeForm.unit_type === 'flat' ? (surchargeForm.quantity || 1) : 1),
                              lang
                            )}
                          </strong>
                          {surchargeForm.unit_type === 'per_person' && ' (× số người)'}
                        </p>
                      )}
                      <button
                        onClick={() => {
                          if (!surchargeForm.label || !surchargeForm.amount_per_unit) return;
                          addSurcharge.mutate({
                            paymentId: drawerPayment.id,
                            body: {
                              label: surchargeForm.label,
                              amount_per_unit: parseFloat(surchargeForm.amount_per_unit),
                              unit_type: surchargeForm.unit_type,
                              quantity: surchargeForm.unit_type === 'flat' ? (surchargeForm.quantity || 1) : 1,
                            },
                          });
                        }}
                        disabled={!surchargeForm.label || !surchargeForm.amount_per_unit || addSurcharge.isPending}
                        className="w-full h-9 rounded-lg bg-violet-600 text-white text-[13px] font-medium hover:bg-violet-700 transition-colors disabled:opacity-40"
                      >
                        {addSurcharge.isPending ? 'Đang thêm…' : 'Thêm phụ phí'}
                      </button>
                    </div>
                  </DrawerSection>

                  {/* Vacation — renter away but keeps the room */}
                  <DrawerSection title="Nghỉ (vắng nhưng giữ phòng)">
                    <p className="text-[12px] text-stone-500 mb-2">
                      Ghi nhận kỳ nghỉ để giảm tiền phòng và dịch vụ theo số ngày vắng. Áp dụng cho mọi tháng
                      chưa thanh toán trùng với kỳ nghỉ.
                    </p>

                    {/* Recorded vacations for this contract */}
                    {vacations.filter(v => v.status === 'active').length > 0 && (
                      <div className="space-y-2 mb-3">
                        {vacations.filter(v => v.status === 'active').map(v => {
                          const fmt = (d) => d ? d.split('-').reverse().join('/') : '—';
                          return (
                            <div key={v.id} className="flex items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
                              <div className="min-w-0">
                                <div className="text-[12px] font-semibold text-amber-800">
                                  {v.scope === 'whole_room' ? 'Cả phòng' : `Một số người (${v.away_count || 0})`}
                                  {' · '}−{Number(v.rent_discount_percent || 0)}% tiền phòng
                                </div>
                                <div className="text-[11px] text-amber-700/80">
                                  {fmt(v.start_date)} → {fmt(v.end_date)}
                                  {v.reason ? ` · ${v.reason}` : ''}
                                </div>
                              </div>
                              <button
                                onClick={() => { if (window.confirm('Hủy kỳ nghỉ này?')) cancelVacation.mutate(v.id); }}
                                disabled={cancelVacation.isPending}
                                className="flex-shrink-0 text-[11px] text-rose-600 hover:text-rose-700 hover:underline disabled:opacity-40"
                              >
                                Hủy
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <button
                      onClick={() => setShowVacation(true)}
                      className="w-full h-9 rounded-lg bg-amber-500 text-white text-[13px] font-medium hover:bg-amber-600 transition-colors"
                    >
                      🏖 Ghi nhận nghỉ
                    </button>
                  </DrawerSection>

                  {/* Prepaid block — renter pays N months of rent + services up front.
                      Needs contract_id: unlike vacations there is no payment-scoped
                      endpoint that can resolve the contract server-side. */}
                  {drawerPayment.contract_id && (
                  <DrawerSection title="Trả trước nhiều tháng">
                    <p className="text-[12px] text-stone-500 mb-2">
                      Thu tiền phòng + dịch vụ của nhiều tháng vào một kỳ. Các tháng được bao
                      chỉ còn tính điện nước.
                    </p>

                    {prepayments.filter(p => p.status === 'active').length > 0 && (
                      <div className="space-y-2 mb-3">
                        {prepayments.filter(p => p.status === 'active').map(p => {
                          const fmt = (m) => m ? m.split('-').reverse().join('/') : '—';
                          return (
                            <div key={p.id} className="flex items-start justify-between gap-2 rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2">
                              <div className="min-w-0">
                                <div className="text-[12px] font-semibold text-teal-800">
                                  {p.months} tháng · {Number(p.total_amount || 0).toLocaleString('vi-VN')} ₫
                                </div>
                                <div className="text-[11px] text-teal-700/80">
                                  {fmt(p.start_month)} → {fmt(p.end_month)}
                                  {p.notes ? ` · ${p.notes}` : ''}
                                </div>
                              </div>
                              <button
                                onClick={() => { if (window.confirm(`Hủy kỳ trả trước ${p.months} tháng? Các tháng sẽ quay lại tính theo từng tháng.`)) cancelPrepayment.mutate(p.id); }}
                                disabled={cancelPrepayment.isPending}
                                className="flex-shrink-0 text-[11px] text-rose-600 hover:text-rose-700 hover:underline disabled:opacity-40"
                              >
                                Hủy
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {showPrepay ? (
                      <div className="rounded-lg border border-stone-200 p-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">Thu từ tháng</label>
                            <input
                              type="month"
                              value={prepayForm.start_month}
                              onChange={e => setPrepayForm(f => ({ ...f, start_month: e.target.value }))}
                              className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">Số tháng</label>
                            <input
                              type="number" min={1}
                              value={prepayForm.months}
                              onChange={e => setPrepayForm(f => ({ ...f, months: Math.max(1, parseInt(e.target.value) || 1) }))}
                              className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">Ưu đãi trả trước (₫)</label>
                          <input
                            type="number" min={0} step={1000}
                            value={prepayForm.discount_amount}
                            onChange={e => setPrepayForm(f => ({ ...f, discount_amount: Math.max(0, parseFloat(e.target.value) || 0) }))}
                            className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white"
                          />
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => setShowPrepay(false)}
                            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-600 hover:bg-stone-50"
                          >
                            Hủy
                          </button>
                          <button
                            onClick={() => addPrepayment.mutate({
                              contractId: drawerPayment.contract_id,
                              body: {
                                start_month: prepayForm.start_month,
                                months: parseInt(prepayForm.months) || 1,
                                discount_amount: parseFloat(prepayForm.discount_amount) || 0,
                              },
                            })}
                            disabled={!/^\d{4}-\d{2}$/.test(prepayForm.start_month) || addPrepayment.isPending}
                            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 disabled:opacity-40"
                          >
                            {addPrepayment.isPending ? 'Đang lưu…' : 'Ghi nhận'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setPrepayForm({ start_month: drawerPayment.month || '', months: 3, discount_amount: 0 });
                          setShowPrepay(true);
                        }}
                        className="w-full h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors"
                      >
                        💰 Ghi nhận trả trước
                      </button>
                    )}
                  </DrawerSection>
                  )}
                </>
              )}

              {/* ── Detail tab ── */}
              {drawerTab === 'detail' && <>
              {/* Status + quick actions */}
              <DrawerSection>
                <div className="flex items-center justify-between gap-3">
                  <StatusPill status={drawerPayment.status} />
                  {drawerPayment.status === 'pending' && (
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => setConfirmAction({ payment: drawerPayment, action: 'reject' })}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 text-[12px] font-medium hover:bg-red-50 transition-colors"
                      >
                        <Icons.XCircle size={12} />
                        {t('finance:payment.action.reject')}
                      </button>
                      <button
                        onClick={() => setConfirmAction({ payment: drawerPayment, action: 'confirm' })}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-600 text-white text-[12px] font-medium hover:bg-green-700 transition-colors"
                      >
                        <Icons.CheckCircle size={12} />
                        {t('finance:payment.action.confirm')}
                      </button>
                    </div>
                  )}
                  {['confirmed', 'rejected', 'partial'].includes(drawerPayment.status) && (
                    <button
                      onClick={() => setConfirmAction({ payment: drawerPayment, action: 'revert' })}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stone-300 text-stone-600 text-[12px] font-medium hover:bg-stone-100 transition-colors"
                    >
                      <Icons.Refresh size={12} />
                      Hoàn tác
                    </button>
                  )}
                </div>
              </DrawerSection>

              {/* Tenant info */}
              <DrawerSection title={t('finance:payment.drawer.section.info')}>
                <KVList items={[
                  [t('finance:payment.drawer.kv.renter'), drawerPayment.renter_name || '—'],
                  [t('finance:payment.drawer.kv.room'),   drawerPayment.room_number || '—'],
                  [t('finance:payment.drawer.kv.month'),  drawerPayment.month || '—'],
                  ...(drawerPayment.confirmed_by
                    ? [[t('finance:payment.drawer.kv.confirmedBy'), drawerPayment.confirmed_by]]
                    : []),
                ]} />
              </DrawerSection>

              {/* ── Itemized breakdown ── */}
              {(() => {
                // Detect prorated payment from admin_note
                // Note format: "Điều chỉnh kết thúc hợp đồng (pay_first): tỷ lệ 18/30 ngày. Ngày rời: 2026-06-18"
                const note = drawerPayment.admin_note || '';
                const prorateMatch = note.match(/tỷ lệ (\d+)\/(\d+) ngày/);
                const moveoutMatch = note.match(/Ngày rời: (\d{4}-\d{2}-\d{2})/);
                const isProrated = !!prorateMatch;
                const proratedDaysUsed = prorateMatch ? parseInt(prorateMatch[1]) : null;
                const proratedDaysInMonth = prorateMatch ? parseInt(prorateMatch[2]) : null;
                const moveoutDate = moveoutMatch ? moveoutMatch[1].split('-').reverse().join('/') : null;

                // Parse services_detail from other_costs_detail
                let detail = drawerPayment.other_costs_detail;
                if (typeof detail === 'string') { try { detail = JSON.parse(detail); } catch { detail = {}; } }
                detail = detail || {};

                // Split into fixed services and metered stubs (metered = handled by meter readings below)
                const meteredKeys = new Set(['electricity', 'electric', 'water']);
                // Discount lines: onboarding/commission + any vacation_* line (dynamic keys keyed by vacation id)
                const isDiscountKey = (k) => ['first_month_discount','commission_amount'].includes(k) || String(k).startsWith('vacation_');
                // prepaid_block is a zero-priced marker, not a charge — surfaced as a
                // badge below instead of an empty 0 ₫ row in the service list.
                const fixedServices = Object.entries(detail).filter(([k]) => !meteredKeys.has(k) && !isDiscountKey(k) && k !== 'prepaid_block');
                const discounts = Object.entries(detail).filter(([k]) => isDiscountKey(k));
                const hasVacation = Object.keys(detail).some((k) => String(k).startsWith('vacation_'));

                // Prepaid block: same key for both sides, distinguished by type.
                const prepaidLine = detail['prepaid_block'] || null;
                const prepaidMonths = prepaidLine?.prepaid_months ?? prepaidLine?.multiplier ?? null;
                const isPrepaidBilling = prepaidLine?.type === 'prepaid_block';
                const isPrepaidCovered = prepaidLine?.type === 'prepaid_covered';

                // Meter readings: only verified, non-base
                const verifiedMeters = meterReadings.filter(m => m.status === 'verified' && !m.is_base_reading);
                const elec = verifiedMeters.filter(m => m.reading_type === 'electricity' || m.reading_type === 'electric');
                const water = verifiedMeters.filter(m => m.reading_type === 'water');

                const meterTotal = verifiedMeters.reduce((s, m) => s + (parseFloat(m.total_cost) || 0), 0);
                const fixedTotal = fixedServices.reduce((s, [, v]) => s + (parseFloat(v?.price ?? v?.amount ?? 0) || 0), 0);
                const discountTotal = discounts.reduce((s, [, v]) => s + (parseFloat(v?.price ?? v?.amount ?? 0) || 0), 0);
                const baseRent = parseFloat(drawerPayment.base_rent ?? drawerPayment.invoice_amount ?? 0) - parseFloat(drawerPayment.other_costs ?? 0);

                return (
                  <>
                    {/* Rent */}
                    <DrawerSection title={
                      <span className="flex items-center gap-2">
                        Chi tiết hóa đơn
                        {isProrated && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-semibold">
                            ✂ {proratedDaysUsed}/{proratedDaysInMonth} ngày
                            {moveoutDate && <span className="font-normal opacity-70">· rời {moveoutDate}</span>}
                          </span>
                        )}
                        {hasVacation && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                            🏖 Nghỉ
                          </span>
                        )}
                        {isPrepaidBilling && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-800 text-[10px] font-semibold">
                            💰 Trả trước {prepaidMonths} tháng
                          </span>
                        )}
                        {isPrepaidCovered && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 text-[10px] font-semibold">
                            ✓ Đã trả trước
                          </span>
                        )}
                      </span>
                    }>
                      {/* Prepaid banner — the single most important thing about this bill */}
                      {prepaidLine && (
                        <div className={`mb-3 rounded-lg px-3 py-2 border ${isPrepaidBilling ? 'bg-teal-50 border-teal-200' : 'bg-teal-50/50 border-teal-100'}`}>
                          <p className="text-[12px] font-semibold text-teal-800">{prepaidLine.name}</p>
                          <p className="text-[11px] text-teal-700/80 mt-0.5">
                            {isPrepaidBilling
                              ? `Hóa đơn này đã gộp tiền phòng và dịch vụ của ${prepaidMonths} tháng. Các tháng sau chỉ còn tính điện nước.`
                              : 'Tiền phòng và dịch vụ tháng này đã thu ở kỳ trả trước. Hóa đơn này chỉ gồm điện nước.'}
                          </p>
                        </div>
                      )}
                      <div className="space-y-0">
                        {/* Base rent */}
                        <div className="flex items-baseline justify-between py-2 border-b border-stone-50">
                          <span className="text-[13px] text-stone-700 font-medium">Tiền thuê cơ bản</span>
                          <span className="text-[13px] font-semibold text-stone-900 tabular-nums">{VND(drawerPayment.base_rent ?? baseRent, lang)}</span>
                        </div>

                        {/* Fixed services */}
                        {fixedServices.length > 0 && (
                          <>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 pt-2 pb-1">Dịch vụ cố định</p>
                            {fixedServices.map(([key, svc]) => (
                              <div key={key} className="flex items-baseline justify-between py-1.5 border-b border-stone-50 last:border-0">
                                <div className="flex-1 min-w-0 mr-2">
                                  <span className="text-[12px] text-stone-600">{svc?.name || key}</span>
                                  {svc?.multiplier > 1 && (
                                    <span className="text-[11px] text-stone-400 ml-1.5">
                                      × {svc.multiplier} {svc.unit === 'person' ? 'người' : svc.unit}
                                    </span>
                                  )}
                                </div>
                                <span className="text-[12px] text-stone-700 tabular-nums">{VND(svc?.price ?? svc?.amount ?? 0, lang)}</span>
                              </div>
                            ))}
                          </>
                        )}

                        {/* Meter readings — electricity & water */}
                        {(elec.length > 0 || water.length > 0) && (
                          <>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 pt-2 pb-1">Điện — Nước</p>
                            {elec.map((m, i) => (
                              <div key={i} className="flex items-start justify-between py-1.5 border-b border-stone-50">
                                <div className="flex-1 min-w-0 mr-2">
                                  <span className="text-[12px] text-stone-600 flex items-center gap-1">
                                    <Icons.Zap size={11} className="text-amber-500 flex-shrink-0" /> Tiền điện
                                  </span>
                                  <span className="text-[11px] text-stone-400 ml-4 block">
                                    {(m.adjusted_reading ?? m.previous_reading ?? 0).toLocaleString(intlLocale(lang))} → {(m.adjusted_reading ?? m.reading_value ?? 0).toLocaleString(intlLocale(lang))} = {(m.usage_amount ?? 0).toLocaleString(intlLocale(lang))} kWh × {VND(m.unit_price ?? 0, lang)}
                                  </span>
                                  <span className="text-[10px] text-stone-300 ml-4 block mt-0.5">
                                    Chỉ số tháng {m.month ? m.month.split('-').reverse().join('/') : '—'}
                                    {m.reading_date && ` · ghi ngày ${new Date(m.reading_date).toLocaleDateString(intlLocale(lang), { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                                  </span>
                                </div>
                                <span className="text-[12px] text-teal-700 font-semibold tabular-nums mt-0.5">{VND(m.total_cost ?? 0, lang)}</span>
                              </div>
                            ))}
                            {water.map((m, i) => (
                              <div key={i} className="flex items-start justify-between py-1.5 border-b border-stone-50">
                                <div className="flex-1 min-w-0 mr-2">
                                  <span className="text-[12px] text-stone-600 flex items-center gap-1">
                                    <Icons.Droplets size={11} className="text-blue-500 flex-shrink-0" /> Tiền nước
                                  </span>
                                  <span className="text-[11px] text-stone-400 ml-4 block">
                                    {(m.previous_reading ?? 0).toLocaleString(intlLocale(lang))} → {(m.adjusted_reading ?? m.reading_value ?? 0).toLocaleString(intlLocale(lang))} = {(m.usage_amount ?? 0).toLocaleString(intlLocale(lang))} m³ × {VND(m.unit_price ?? 0, lang)}
                                  </span>
                                  <span className="text-[10px] text-stone-300 ml-4 block mt-0.5">
                                    Chỉ số tháng {m.month ? m.month.split('-').reverse().join('/') : '—'}
                                    {m.reading_date && ` · ghi ngày ${new Date(m.reading_date).toLocaleDateString(intlLocale(lang), { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                                  </span>
                                </div>
                                <span className="text-[12px] text-teal-700 font-semibold tabular-nums mt-0.5">{VND(m.total_cost ?? 0, lang)}</span>
                              </div>
                            ))}
                            {verifiedMeters.length === 0 && (meterReadings.some(m => !m.is_base_reading)) && (
                              <p className="text-[11px] text-amber-600 py-1">Có chỉ số nhưng chưa xác nhận</p>
                            )}
                            {meterReadings.filter(m => !m.is_base_reading).length === 0 && (
                              <p className="text-[11px] text-stone-400 italic py-1">Chưa nhập chỉ số điện/nước tháng này</p>
                            )}
                          </>
                        )}

                        {/* Discounts */}
                        {discounts.length > 0 && (
                          <>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 pt-2 pb-1">Chiết khấu</p>
                            {discounts.map(([key, svc]) => (
                              <div key={key} className="flex items-baseline justify-between py-1.5">
                                <span className="text-[12px] text-rose-600">{svc?.name || key}</span>
                                <span className="text-[12px] font-semibold text-rose-600 tabular-nums">{VND(svc?.price ?? svc?.amount ?? 0, lang)}</span>
                              </div>
                            ))}
                          </>
                        )}

                        {/* Subtotal — invoice before surcharges */}
                        <div className="flex items-baseline justify-between py-2 mt-1 border-t-2 border-stone-200">
                          <span className="text-[13px] font-semibold text-stone-800">Hóa đơn gốc</span>
                          <span className="text-[14px] font-bold text-stone-900 tabular-nums">{VND(drawerPayment.invoice_amount, lang)}</span>
                        </div>

                        {/* Surcharges — shown inline so the final total is self-explanatory */}
                        {drawerPayment.surcharges?.length > 0 && (() => {
                          const surcharges = Array.isArray(drawerPayment.surcharges)
                            ? drawerPayment.surcharges
                            : (typeof drawerPayment.surcharges === 'string'
                                ? (() => { try { return JSON.parse(drawerPayment.surcharges); } catch { return []; } })()
                                : []);
                          const surchargesSum = surcharges.reduce((s, x) => s + (parseFloat(x.total ?? x.amount ?? 0) || 0), 0);
                          if (surchargesSum === 0) return null;
                          return (
                            <>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400 pt-2 pb-1">Phụ phí</p>
                              {surcharges.map((s, i) => (
                                <div key={i} className="flex items-baseline justify-between py-1.5 border-b border-stone-50 last:border-0">
                                  <div className="flex-1 min-w-0 mr-2">
                                    <span className="text-[12px] text-stone-600">{s.label || s.name || 'Phụ phí'}</span>
                                    {s.unit_type === 'per_person' && s.quantity > 1 && (
                                      <span className="text-[11px] text-stone-400 ml-1.5">
                                        {VND(s.amount_per_unit ?? 0, lang)}/người × {s.quantity} người
                                      </span>
                                    )}
                                    {s.unit_type === 'flat' && s.quantity > 1 && (
                                      <span className="text-[11px] text-stone-400 ml-1.5">
                                        {VND(s.amount_per_unit ?? 0, lang)} × {s.quantity}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[12px] font-semibold text-purple-700 tabular-nums">
                                    {VND(s.total ?? s.amount ?? 0, lang)}
                                  </span>
                                </div>
                              ))}
                              <div className="flex items-baseline justify-between py-2 mt-1 border-t-2 border-stone-200">
                                <span className="text-[13px] font-semibold text-stone-800">Tổng cộng</span>
                                <span className="text-[14px] font-bold text-stone-900 tabular-nums">
                                  {VND((drawerPayment.invoice_amount ?? 0) + surchargesSum, lang)}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </DrawerSection>

                    {/* Payment summary */}
                    <DrawerSection title={t('finance:payment.drawer.section.amounts')}>
                      <KVList items={[
                        ...(drawerPayment.final_amount && drawerPayment.final_amount !== drawerPayment.invoice_amount
                          ? [[t('finance:payment.drawer.kv.finalAmount'), VND(drawerPayment.final_amount, lang)]]
                          : []),
                        [t('finance:payment.drawer.kv.paidAmount'),  VND(drawerPayment.paid_amount, lang)],
                        [t('finance:payment.drawer.kv.outstanding'), VND(
                          Math.max(0,
                            (drawerPayment.final_amount ?? drawerPayment.invoice_amount ?? 0) -
                            (drawerPayment.paid_amount ?? 0)
                          ), lang,
                        )],
                      ]} />
                    </DrawerSection>
                  </>
                );
              })()}

              {/* Notes */}
              {(drawerPayment.renter_notes || drawerPayment.admin_note) && (
                <DrawerSection title={t('finance:payment.drawer.section.notes')}>
                  {drawerPayment.renter_notes && (
                    <div className="mb-3">
                      <p className="text-[11px] font-medium text-stone-400 mb-1">{t('finance:payment.drawer.noteBy.renter')}</p>
                      <p className="text-[13px] text-stone-700">{drawerPayment.renter_notes}</p>
                    </div>
                  )}
                  {drawerPayment.admin_note && (
                    <div>
                      <p className="text-[11px] font-medium text-stone-400 mb-1">{t('finance:payment.drawer.noteBy.admin')}</p>
                      <p className="text-[13px] text-stone-700">{drawerPayment.admin_note}</p>
                    </div>
                  )}
                </DrawerSection>
              )}

              {/* Transaction history */}
              <DrawerSection title={t('finance:payment.drawer.section.history')}>
                {!txList ? (
                  <div className="space-y-2">
                    {[1, 2].map(i => (
                      <div key={i} className="h-10 bg-stone-100 rounded animate-pulse" />
                    ))}
                  </div>
                ) : txList.length === 0 ? (
                  <p className="text-[13px] text-stone-400">{t('finance:payment.drawer.history.empty')}</p>
                ) : (
                  <div className="space-y-1">
                    {txList.map((tx, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 py-2.5 border-b border-stone-100 last:border-0"
                      >
                        <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                          <Icons.CreditCard size={12} className="text-stone-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-stone-900">{VND(tx.amount, lang)}</p>
                          {tx.created_at && (
                            <p className="text-[11px] text-stone-400">
                              {new Date(tx.created_at).toLocaleDateString(intlLocale(lang), {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                              })}
                            </p>
                          )}
                          {tx.note && (
                            <p className="text-[12px] text-stone-500 mt-0.5 truncate">{tx.note}</p>
                          )}
                        </div>
                        {(tx.status || tx.payment_status) && (
                          <StatusPill status={tx.status ?? tx.payment_status} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </DrawerSection>

              {/* Proof image */}
              {drawerPayment.proof_url && (
                <DrawerSection title={t('finance:payment.drawer.section.proof')}>
                  <a href={drawerPayment.proof_url} target="_blank" rel="noreferrer" className="block">
                    <img
                      src={drawerPayment.proof_url}
                      alt={t('finance:payment.drawer.proof.alt')}
                      className="w-full rounded-lg border border-stone-200 object-cover max-h-52 hover:opacity-90 transition-opacity"
                    />
                  </a>
                </DrawerSection>
              )}
              </>}
            </DrawerBody>

            {/* Export bill — always available */}
            <DrawerFooter>
              <button
                onClick={handleExportBill}
                disabled={isExporting}
                className="w-full h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-600 hover:bg-stone-50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <Icons.Download size={13} />
                {isExporting ? 'Đang xuất…' : 'Xuất hóa đơn (PNG)'}
              </button>
            </DrawerFooter>

            {drawerPayment.status === 'pending' && (
              <DrawerFooter>
                <button
                  onClick={() => setConfirmAction({ payment: drawerPayment, action: 'reject' })}
                  className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
                >
                  {t('finance:payment.action.reject')}
                </button>
                <button
                  onClick={() => setConfirmAction({ payment: drawerPayment, action: 'confirm' })}
                  className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors"
                >
                  {t('finance:payment.action.confirm')}
                </button>
              </DrawerFooter>
            )}
            {['confirmed', 'rejected', 'partial'].includes(drawerPayment.status) && (
              <DrawerFooter>
                <button
                  onClick={() => setConfirmAction({ payment: drawerPayment, action: 'revert' })}
                  className="flex-1 h-9 rounded-lg border border-stone-300 text-[13px] font-medium text-stone-600 hover:bg-stone-50 transition-colors"
                >
                  <span className="flex items-center justify-center gap-1.5">
                    <Icons.Refresh size={13} />
                    Hoàn tác về chờ xử lý
                  </span>
                </button>
              </DrawerFooter>
            )}
          </>
        )}
      </Drawer>

      {/* Off-screen bill card — mounted so html-to-image can capture it */}
      {drawerPayment && (
        <div style={{ position: 'fixed', left: -9999, top: -9999, pointerEvents: 'none' }}
             aria-hidden="true">
          <div ref={billCardRef}>
            <BillExportCard payment={drawerPayment} meterReadings={meterReadings} />
          </div>
        </div>
      )}

      {/* ── Confirm modal ── */}
      {confirmAction && (
        <ConfirmModal
          confirmAction={confirmAction}
          onClose={() => setConfirmAction(null)}
          onSubmit={data => updateStatus.mutate({ id: confirmAction.payment.id, ...data })}
          loading={updateStatus.isPending}
        />
      )}

      {/* ── Manual payment modal ── */}
      {showManual && (
        <ManualPaymentModal
          onClose={() => setShowManual(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['payments-admin'] });
            setShowManual(false);
          }}
        />
      )}

      {/* ── Generate invoices by building policy modal ── */}
      {showGenerate && (
        <GenerateInvoicesModal
          onClose={() => setShowGenerate(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['payments-admin'] });
            setShowGenerate(false);
          }}
        />
      )}

      {/* ── Bulk surcharge modal ── */}
      {showBulkSurcharge && (
        <BulkSurchargeModal
          defaultMonth={month}
          defaultBuildingId={buildingId}
          buildings={buildings}
          onClose={() => setShowBulkSurcharge(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['payments-admin'] });
            setShowBulkSurcharge(false);
            setSelected(new Set());
          }}
        />
      )}

      {/* ── Vacation modal ── */}
      {showVacation && drawerPayment && (
        <VacationModal
          payment={drawerPayment}
          onClose={() => setShowVacation(false)}
          onSubmit={(body) => addVacation.mutate({ paymentId: drawerPayment.id, body })}
          loading={addVacation.isPending}
        />
      )}
    </div>
  );
}

// ── Vacation (renter away, keeps room) modal ──────────────────────────────────
function VacationModal({ payment, onClose, onSubmit, loading }) {
  // Default the date range to the payment's month
  const monthStr = payment?.month || '';
  const [y, m] = monthStr.split('-').map(Number);
  const firstDay = y && m ? `${monthStr}-01` : '';
  const lastDay = y && m ? `${monthStr}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}` : '';

  const [scope, setScope] = useState('whole_room');
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [awayCount, setAwayCount] = useState(1);
  const [percent, setPercent] = useState(100);
  const [reason, setReason] = useState('');

  const valid =
    startDate && endDate && endDate >= startDate &&
    percent >= 0 && percent <= 100 &&
    (scope === 'whole_room' || awayCount >= 1);

  const submit = () => {
    if (!valid || loading) return;
    onSubmit({
      scope,
      start_date: startDate,
      end_date: endDate,
      rent_discount_percent: parseFloat(percent) || 0,
      away_count: scope === 'partial' ? parseInt(awayCount) || 1 : null,
      reason: reason || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-bold text-stone-800 mb-1">🏖 Ghi nhận nghỉ</h3>
        <p className="text-[12px] text-stone-500 mb-4">
          Phòng {payment?.room_number || payment?.room_id} · áp dụng cho các tháng chưa thanh toán trùng kỳ nghỉ
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Phạm vi</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setScope('whole_room')}
                className={`h-9 rounded-lg text-[13px] font-medium border transition-colors ${scope === 'whole_room' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-stone-600 border-stone-200'}`}
              >
                Cả phòng
              </button>
              <button
                type="button"
                onClick={() => setScope('partial')}
                className={`h-9 rounded-lg text-[13px] font-medium border transition-colors ${scope === 'partial' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-stone-600 border-stone-200'}`}
              >
                Một số người
              </button>
            </div>
            <p className="text-[11px] text-stone-400 mt-1">
              {scope === 'whole_room'
                ? 'Miễn toàn bộ dịch vụ theo số ngày vắng + giảm tiền phòng.'
                : 'Chỉ miễn dịch vụ theo người của những người vắng; dịch vụ theo phòng giữ nguyên.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Từ ngày</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Đến ngày</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" />
            </div>
          </div>

          {scope === 'partial' && (
            <div>
              <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Số người vắng</label>
              <input type="number" min={1} value={awayCount}
                onChange={e => setAwayCount(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" />
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">% giảm tiền phòng</label>
            <input type="number" min={0} max={100} value={percent}
              onChange={e => setPercent(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
              className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" />
            <p className="text-[11px] text-stone-400 mt-1">
              Giảm = % × (số ngày vắng / số ngày trong tháng) × tiền phòng.
            </p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Ghi chú</label>
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="VD: Nghỉ hè về quê"
              className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-600 hover:bg-stone-50">
            Hủy
          </button>
          <button onClick={submit} disabled={!valid || loading}
            className="flex-1 h-9 rounded-lg bg-amber-500 text-white text-[13px] font-medium hover:bg-amber-600 disabled:opacity-40">
            {loading ? 'Đang lưu…' : 'Ghi nhận'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Generate invoices by building policy modal ────────────────────────────────
function GenerateInvoicesModal({ onClose, onSuccess }) {
  const qc = useQueryClient();
  const nowYM = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const nextYM = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const [buildingId, setBuildingId] = useState('');
  const [fromMonth, setFromMonth]   = useState(nowYM());
  const [toMonth, setToMonth]       = useState(nextYM());
  const [updateExisting, setUpdateExisting] = useState(false);
  const [result, setResult] = useState(null);

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings-list-gen'],
    queryFn: () => api.get('/api/properties/buildings').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.buildings ?? []);
    }),
    staleTime: 10 * 60 * 1000,
  });

  const generate = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams({ building_id: buildingId, update_existing: updateExisting });
      if (fromMonth) params.set('from_month', fromMonth);
      if (toMonth)   params.set('to_month', toMonth);
      return api.post(`/api/admin/payments/generate-by-building?${params}`).then(r => r.data);
    },
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ['payments-admin'] });
      if (data.commission_errors > 0) {
        toast.error(`Tạo hóa đơn xong nhưng ${data.commission_errors} chi phí hoa hồng thất bại`);
      } else {
        toast.success(`Tạo xong: ${data.payments_created} mới, ${data.payments_updated} cập nhật${data.commission_expenses > 0 ? `, ${data.commission_expenses} chi phí hoa hồng` : ''}`);
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Tạo hóa đơn thất bại'),
  });

  const inputCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const labelCls = 'block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-base font-bold text-white">Tạo hóa đơn theo chính sách toà nhà</h2>
            <p className="text-teal-100 text-xs mt-0.5">
              Áp dụng ngày xuất hoá đơn của toà nhà cho tất cả hợp đồng đang hoạt động
            </p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10">
            <Icons.X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Building */}
          <div>
            <label className={labelCls}>Toà nhà *</label>
            <select className={inputCls} value={buildingId} onChange={e => setBuildingId(e.target.value)}>
              <option value="">Chọn toà nhà…</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.billing_day ? ` — xuất HĐ ngày ${b.billing_day}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Month range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Từ tháng</label>
              <input type="month" className={inputCls} value={fromMonth} onChange={e => setFromMonth(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Đến tháng (bao gồm)</label>
              <input type="month" className={inputCls} value={toMonth} onChange={e => setToMonth(e.target.value)} />
              <p className="text-[11px] text-teal-600 mt-1">Mặc định: tháng tới (tạo trước hóa đơn N+1)</p>
            </div>
          </div>

          {/* Update existing option */}
          <label className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100 cursor-pointer hover:bg-amber-100/60 transition-colors">
            <input
              type="checkbox"
              checked={updateExisting}
              onChange={e => setUpdateExisting(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-stone-300 text-teal-600"
            />
            <div>
              <p className="text-[13px] font-semibold text-amber-800">Cập nhật hóa đơn đã có</p>
              <p className="text-[11px] text-amber-600 mt-0.5">
                Tính lại ngày hạn theo chính sách toà nhà cho các hóa đơn chưa thanh toán/chưa xác nhận.
                Không ảnh hưởng đến hóa đơn đã xác nhận hoặc thanh toán một phần.
              </p>
            </div>
          </label>

          {/* Result summary */}
          {result && (
            <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-[12px] space-y-1 text-teal-800">
              <p className="font-semibold text-teal-900 mb-1">Kết quả</p>
              <p>Kỳ: <strong>{result.months_range}</strong></p>
              <p>Hợp đồng đã xử lý: <strong>{result.contracts_found}</strong></p>
              <div className="flex gap-4 mt-1">
                <span>✅ Tạo mới: <strong>{result.payments_created}</strong></span>
                <span>🔄 Cập nhật: <strong>{result.payments_updated}</strong></span>
                <span>⏭ Bỏ qua: <strong>{result.payments_skipped}</strong></span>
              </div>
              {result.commission_expenses > 0 && (
                <p className="text-teal-700">💰 Chi phí hoa hồng: <strong>{result.commission_expenses}</strong></p>
              )}
              {result.commission_errors > 0 && (
                <p className="text-red-600 font-semibold">⚠️ Lỗi tạo chi phí hoa hồng: {result.commission_errors} hợp đồng. Kiểm tra log dịch vụ.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-3 bg-stone-50/50">
          <button onClick={onClose}
            className="px-4 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            {result ? 'Đóng' : 'Huỷ'}
          </button>
          {!result && (
            <button
              onClick={() => generate.mutate()}
              disabled={!buildingId || generate.isPending}
              className="flex items-center gap-1.5 px-5 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              <Icons.Sparkles size={14} />
              {generate.isPending ? 'Đang tạo…' : 'Tạo hóa đơn'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Bulk Surcharge Modal ───────────────────────────────────────────────────────
function BulkSurchargeModal({ defaultMonth, defaultBuildingId, buildings, onClose, onSuccess }) {
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';

  const [buildingId, setBuildingId] = useState(defaultBuildingId || '');
  const [month, setMonth] = useState(defaultMonth || nowYM());
  const [label, setLabel] = useState('');
  const [amountPerUnit, setAmountPerUnit] = useState('');
  const [unitType, setUnitType] = useState('flat');
  const [quantity, setQuantity] = useState(1);
  const [result, setResult] = useState(null);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/api/admin/payments/bulk-surcharge', {
        building_id: parseInt(buildingId),
        month,
        label,
        amount_per_unit: parseFloat(amountPerUnit),
        unit_type: unitType,
        quantity: unitType === 'flat' ? quantity : undefined,
      }).then(r => r.data),
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ['payments-admin'] });
      toast.success(`Đã thêm phụ phí cho ${data.updated} hóa đơn`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Thêm phụ phí thất bại'),
  });

  const inputCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400';
  const labelCls = 'block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5';
  const canSubmit = buildingId && month && label && amountPerUnit && !submit.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-violet-700 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-base font-bold text-white">Phụ phí hàng loạt</h2>
            <p className="text-violet-100 text-xs mt-0.5">
              Áp dụng cùng một phụ phí cho tất cả hóa đơn của toà nhà trong tháng
            </p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10">
            <Icons.X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Building + Month */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Toà nhà *</label>
              <select className={inputCls} value={buildingId} onChange={e => setBuildingId(e.target.value)}>
                <option value="">Chọn toà nhà…</option>
                {buildings.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Tháng *</label>
              <input type="month" className={inputCls} value={month} onChange={e => setMonth(e.target.value)} />
            </div>
          </div>

          {/* Label */}
          <div>
            <label className={labelCls}>Tên phụ phí *</label>
            <input
              className={inputCls}
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="VD: Phí tạm trú, Phí vệ sinh tháng…"
            />
          </div>

          {/* Amount + type */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Đơn giá *</label>
              <CurrencyInput
                value={amountPerUnit}
                onChange={e => setAmountPerUnit(e.target.value)}
                className={inputCls}
                placeholder="0"
              />
            </div>
            <div>
              <label className={labelCls}>Loại</label>
              <select className={inputCls} value={unitType} onChange={e => setUnitType(e.target.value)}>
                <option value="flat">Cố định</option>
                <option value="per_person">Theo người (tạm trú…)</option>
              </select>
            </div>
          </div>

          {unitType === 'flat' && (
            <div>
              <label className={labelCls}>Số lượng</label>
              <input
                type="number"
                min={1}
                className={inputCls}
                value={quantity}
                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              />
            </div>
          )}

          {unitType === 'per_person' && (
            <div className="p-3 bg-violet-50 border border-violet-100 rounded-lg">
              <p className="text-[12px] text-violet-800 font-medium">Ví dụ: Tạm trú</p>
              <p className="text-[11px] text-violet-600 mt-0.5">
                Số người sẽ tự động lấy từ hợp đồng của từng phòng.
                Mỗi phòng có số người khác nhau sẽ được tính riêng.
              </p>
            </div>
          )}

          {amountPerUnit && label && unitType === 'flat' && (
            <p className="text-[12px] text-stone-500">
              Mỗi hóa đơn sẽ tăng thêm:{' '}
              <strong className="text-violet-700">
                {VND(parseFloat(amountPerUnit || 0) * quantity, lang)}
              </strong>
            </p>
          )}

          {/* Result */}
          {result && (
            <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg text-[12px] text-violet-800 space-y-1">
              <p className="font-semibold text-violet-900">Kết quả</p>
              <p>Tháng: <strong>{result.month}</strong></p>
              <p>Hóa đơn đã cập nhật: <strong>{result.updated}</strong></p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-3 bg-stone-50/50">
          <button
            onClick={onClose}
            className="px-4 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            {result ? 'Đóng' : 'Huỷ'}
          </button>
          {!result && (
            <button
              onClick={() => submit.mutate()}
              disabled={!canSubmit}
              className="flex items-center gap-1.5 px-5 h-9 rounded-lg bg-violet-600 text-white text-[13px] font-medium hover:bg-violet-700 transition-colors disabled:opacity-50"
            >
              <Icons.Plus size={14} />
              {submit.isPending ? 'Đang áp dụng…' : 'Áp dụng phụ phí'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
